import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { ExternalLink } from "lucide-react";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "@/packages/utils";
import { frameworks } from "./dashboard.data";
import type { Framework } from "./dashboard.types";

function getStatus(cov: number) {
  if (cov >= 80)
    return { label: "On Track", className: "text-emerald-600 bg-emerald-50" };
  if (cov >= 50)
    return { label: "At Risk", className: "text-amber-600 bg-amber-50" };
  return { label: "Behind", className: "text-red-600 bg-red-50" };
}

function ProgressBar({
  cov,
  color,
  index,
}: {
  cov: number;
  color: string;
  index: number;
}) {
  return (
    <div className="h-2.5 w-full rounded-sm bg-surface overflow-hidden">
      <motion.div
        className="h-full rounded-sm"
        style={{ backgroundColor: color }}
        initial={{ width: 0 }}
        animate={{ width: `${cov}%` }}
        transition={{ ...springs.smooth, delay: index * 0.06 }}
      />
    </div>
  );
}

/** Expanded detail: 2-line grid with metrics, trigger, status + link */
function ExpandedDetail({ fw }: { fw: Framework }) {
  const { workspaceSlug } = useParams({ strict: false });
  const status = getStatus(fw.cov);

  return (
    <div className="mt-1.5 rounded bg-surface/80 px-2.5 py-2 text-[11px]">
      {/* Line 1: key metrics in a balanced grid */}
      <div className="grid grid-cols-4 gap-x-2 text-secondary">
        <div>
          <span className="text-muted">Controls</span>
          <p className="font-medium text-primary">
            {fw.met}/{fw.ctrl}
          </p>
        </div>
        <div>
          <span className="text-muted">Docs</span>
          <p className="font-medium text-primary">{fw.docCov}%</p>
        </div>
        <div>
          <span className="text-muted">Processes</span>
          <p className="font-medium text-primary">{fw.openProcesses} open</p>
        </div>
        <div>
          <span className="text-muted">Due</span>
          <p className="font-medium text-primary">{fw.due}</p>
        </div>
      </div>

      {/* Line 2: trigger, status badge, link */}
      <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-border/50">
        <div className="flex items-center gap-2">
          {fw.trigger && (
            <span className="text-muted">
              Trigger:{" "}
              <span className="text-secondary font-medium">{fw.trigger}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`font-medium px-1.5 py-0.5 rounded ${status.className}`}
          >
            {status.label}
          </span>
          {fw.auditSlug && workspaceSlug && (
            <Link
              to="/$workspaceSlug/audits/$auditId"
              params={{
                workspaceSlug: workspaceSlug as string,
                auditId: fw.auditSlug,
              }}
              className="text-accent hover:text-accent/80 transition-colors"
              title="Open audit"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

/** Tier 1-2: Full Progress-Cards */
function FullCard({ fw, index }: { fw: Framework; index: number }) {
  const status = getStatus(fw.cov);
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div
        className="flex items-center justify-between px-3 py-2"
        style={{ backgroundColor: `${fw.color}15` }}
      >
        <div className="flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: fw.color }}
          />
          <span className="text-sm font-semibold text-primary">{fw.name}</span>
        </div>
        <span className="text-sm font-bold text-primary">{fw.cov}%</span>
      </div>
      <div className="px-3 pt-1.5 pb-2">
        <ProgressBar cov={fw.cov} color={fw.color} index={index} />
        <div className="mt-2 space-y-1">
          <MetricRow label="Controls" value={`${fw.met} / ${fw.ctrl}`} />
          <MetricRow label="Documents" value={`${fw.docCov}%`} />
          <MetricRow label="Prozesse" value={`${fw.openProcesses} offen`} />
          <MetricRow label="Due" value={fw.due} />
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted">Status</span>
            <span
              className={`text-xs font-medium px-1.5 py-0.5 rounded ${status.className}`}
            >
              {status.label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Tier 3: Condensed Cards */
function CondensedCard({ fw, index }: { fw: Framework; index: number }) {
  const status = getStatus(fw.cov);
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div
        className="flex items-center justify-between px-3 py-1.5"
        style={{ backgroundColor: `${fw.color}15` }}
      >
        <div className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: fw.color }}
          />
          <span className="text-xs font-semibold text-primary">{fw.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-primary">{fw.cov}%</span>
          <span
            className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${status.className}`}
          >
            {status.label}
          </span>
        </div>
      </div>
      <div className="px-3 py-1.5">
        <ProgressBar cov={fw.cov} color={fw.color} index={index} />
        <div className="flex items-center gap-4 mt-1.5 text-xs text-secondary">
          <span>
            Controls: {fw.met}/{fw.ctrl}
          </span>
          <span>Docs: {fw.docCov}%</span>
        </div>
      </div>
    </div>
  );
}

/** Tier 4: Mini single-line cards */
function MiniCard({ fw, index }: { fw: Framework; index: number }) {
  const status = getStatus(fw.cov);
  return (
    <div className="rounded-lg border border-border px-3 py-1.5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: fw.color }}
          />
          <span className="text-xs font-semibold text-primary">{fw.name}</span>
        </div>
        <span className="text-xs font-bold text-primary">{fw.cov}%</span>
      </div>
      <ProgressBar cov={fw.cov} color={fw.color} index={index} />
      <div className="flex items-center justify-between mt-1 text-[11px] text-muted">
        <span>
          {fw.met}/{fw.ctrl} controls
        </span>
        <span className={`font-medium ${status.className} px-1 rounded`}>
          {status.label}
        </span>
      </div>
    </div>
  );
}

/** Tier 5-6+: Compact bar row with Document-Coverage-style expand */
function BarRow({
  fw,
  index,
  expandable,
  expanded,
  onToggle,
}: {
  fw: Framework;
  index: number;
  expandable: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const barContent = (
    <>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-secondary">{fw.name}</span>
        <span className="text-xs font-semibold text-primary">{fw.cov}%</span>
      </div>
      <ProgressBar cov={fw.cov} color={fw.color} index={index} />
    </>
  );

  return (
    <div>
      {expandable ? (
        <button
          type="button"
          className="w-full text-left cursor-pointer hover:bg-surface/60 rounded-lg transition-colors"
          onClick={onToggle}
        >
          {barContent}
        </button>
      ) : (
        <div>{barContent}</div>
      )}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springs.snappy}
            className="overflow-hidden"
          >
            <ExpandedDetail fw={fw} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted">{label}</span>
      <span className="text-xs font-medium text-primary">{value}</span>
    </div>
  );
}

export function FrameworkCoverage() {
  const { t } = useTranslation(["dashboard"]);
  const [expandedSet, setExpandedSet] = useState<Set<number>>(() => new Set());
  const count = frameworks.length;

  const toggleExpand = useCallback((index: number) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  return (
    <div className="rounded-xl bg-card border border-border overflow-hidden h-full flex flex-col">
      <div className="flex items-center px-4 pt-3 pb-3 shrink-0">
        <h3 className="text-sm font-semibold text-primary">
          {t("dashboard:frameworkCoverage.title")}
        </h3>
      </div>

      <div className="framework-body overflow-y-auto flex-1 min-h-0 px-4 pb-2 flex flex-col">
        {count === 0 && (
          <div className="flex items-center justify-center h-full text-sm text-muted">
            No frameworks configured
          </div>
        )}

        {count >= 1 && count <= 2 && (
          <div className="space-y-2">
            {frameworks.map((fw, i) => (
              <FullCard key={fw.name} fw={fw} index={i} />
            ))}
          </div>
        )}

        {count === 3 && (
          <div className="space-y-2">
            {frameworks.map((fw, i) => (
              <CondensedCard key={fw.name} fw={fw} index={i} />
            ))}
          </div>
        )}

        {count === 4 && (
          <div className="space-y-1.5">
            {frameworks.map((fw, i) => (
              <MiniCard key={fw.name} fw={fw} index={i} />
            ))}
          </div>
        )}

        {count >= 5 && (
          <div className="space-y-2.5 my-auto">
            {frameworks.map((fw, i) => (
              <BarRow
                key={fw.name}
                fw={fw}
                index={i}
                expandable={count >= 6}
                expanded={expandedSet.has(i)}
                onToggle={() => toggleExpand(i)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
